import { buildDataProviderId } from '@o2d/datasource';
import type { AxisConfiguration, CrosspointDataProviderConfiguration, ViewportItem } from '@o2d/datasource';
import type { AreaConfiguration, LayoutTemplate, CellHoverEventDetail } from '@o2d/grid-layout';
import type { LitElement } from 'lit';
import { customElement } from 'lit/decorators/custom-element.js';
import { state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import { container } from 'tsyringe';
import { AbstractO2dTimelineView } from '../abstract-o2d-timeline.view';
import { appState } from '../../app.state';
import { AxisDataClient } from '../../connectivity/axis-data-client';
import { createResourcePlanningAxisConfigurations } from './resource-planning-axis.factory';
import { RESOURCE_PLANNING_LAYOUT_TEMPLATE, RESOURCE_PLANNING_SECTION_SIZES } from './resource-planning-layout';
import { createResourcePlanningDataProviders } from './resource-planning-data-providers.factory';
import { createResourcePlanningAreaConfigurations } from './resource-planning-area.factory';
import type { Issue } from '../../domain/entities/issue.entity';
import type { ResourcePlanningItem } from './types/resource-planning-item.type';
import { FilterHelper } from '../../helpers/filter.helper';
import { UiHelper } from '../../helpers/ui.helper';
import { html } from 'lit';
import type { TimelineToolbarItem } from '../../types/timeline-toolbar-item.interface';
import { defaultTimelineToolbarItems } from '../../toolbar-items/o2d-default-toolbar-items';
import { resourceFiltersToolbarItem } from '../../toolbar-items/o2d-resource-filters.toolbar-item';

/**
 * Resource planning timeline view.
 * Extends AbstractO2dTimelineView directly (not AbstractStandardTimelineView)
 * because it uses custom data providers and area renderers for scheduling metadata.
 *
 * Route: /by-resource-planning
 * Y-axis: Employee (sorted by lastName ASC)
 */
@customElement('o2d-resource-planning-view')
export class ResourcePlanningView extends AbstractO2dTimelineView {

    protected readonly viewStateName = 'resource-planning';

    protected readonly contentProviderKey = buildDataProviderId('B', 'D');

    /**
     * Isolate the inherited filter drawer to the resource-specific AppState slot.
     * The base view threads this into the `<ha-form-issue-filters>` mount, so the
     * drawer reads/writes `appState.resourceFilters` (never the ToDo `filters` slot).
     * The drawer open/collapse state deliberately still reuses the shared
     * `appState.todoFilterOpen` gate (see AbstractO2dTimelineView) — only the DATA
     * slot is isolated here.
     */
    protected override filterSlot: 'filters' | 'resourceFilters' = 'resourceFilters';

    /** Set of entity UIDs currently highlighted by the user. */
    @state() private _highlightedUids: Set<string> = new Set();

    /** Precompiled filter predicate — returns true if issue matches active filters. */
    private _filterPredicate: (issue: Issue) => boolean = () => true;

    /** Unsubscribe function for the appState.resourceFilters subscription. */
    private _filterUnsubscriber?: () => void;

    /** Date cursor computed fresh in connectedCallback. */
    private _cursorX!: DateTime;

    /**
     * Clamps dateCursor to today if it is in the past. Resource planning is
     * forward-looking — today is the anchor of all calculations, so scrolling
     * before today is meaningless and therefore disabled via axis range.
     * Computes cursorX, then delegates to super.connectedCallback().
     */
    override async connectedCallback(): Promise<void> {
        const today = DateTime.utc().startOf('day');
        if (appState.dateCursorDate < today) {
            appState.dateCursorDate = today;
        }

        this._cursorX = DateTime.fromSeconds(appState.dateCursor, { zone: 'utc' });
        await super.connectedCallback();
        this.addEventListener('click', this._onHighlightButtonClick);
    }

    override disconnectedCallback(): void {
        super.disconnectedCallback();
        this._filterUnsubscriber?.();
        this.removeEventListener('click', this._onHighlightButtonClick);
    }

    protected createAxisConfigurations(): AxisConfiguration<string>[] {
        return createResourcePlanningAxisConfigurations({
            entityType: 'Employee',
            sort: { strategy: 'property', path: 'lastName', direction: 'ASC' },
            yItemPath: 'employees[*].uid',
            onFirstFetch: (firstShortId: string) => {
                appState.setViewCursorY(this.viewStateName, firstShortId);
            }
        });
    }

    protected createAreaConfigurations(): AreaConfiguration[] {
        if (!this._filterUnsubscriber) {
            if (appState.hasAppliedResourceFilters()) {
                this._filterPredicate = FilterHelper.precompileFilters(appState.resourceFilters, null);
            }
            this._filterUnsubscriber = appState.subscribe(() => {
                if (appState.hasAppliedResourceFilters()) {
                    this._filterPredicate = FilterHelper.precompileFilters(appState.resourceFilters, null);
                } else {
                    this._filterPredicate = () => true;
                }
                this._areaConfigurations = createResourcePlanningAreaConfigurations(
                    (uid) => this._highlightedUids.has(uid),
                    (issue) => !this._filterPredicate(issue)
                );
            }, 'resourceFilters');
        }

        // The milestone half of this predicate is gone: milestones no longer reach this view at
        // all — ResourcesViewportQueryService::loadIssues excludes them from the query. It was
        // never an exclusion anyway. This return value drives ONLY the `filtered-out` CSS class
        // (resource-planning-area.factory.ts), which dims rather than hides, and naming the
        // milestone type here EXEMPTED milestones from that dimming. It read like the opposite
        // of what it did, which is why it is not left standing as a harmless redundancy.
        //
        // The identical-looking clauses elsewhere (abstract-standard-timeline.view.ts,
        // abstract-ha-timeline.view.ts, filter.helper.ts) are LOAD-BEARING and stay: in the
        // standard timelines milestones must remain visible and undimmed, and a test guards it.
        return createResourcePlanningAreaConfigurations(
            (uid) => this._highlightedUids.has(uid),
            (issue) => !this._filterPredicate(issue)
        );
    }

    protected createDataProviderConfigurations(): CrosspointDataProviderConfiguration<any, any, any>[] {
        return createResourcePlanningDataProviders();
    }

    protected createLayoutTemplate(): LayoutTemplate {
        return RESOURCE_PLANNING_LAYOUT_TEMPLATE;
    }

    protected async createStartCursors(): Promise<{ axisId: string; value: string | number }[]> {
        let yStartCursor = this.getYAxisStartCursor();
        if (!yStartCursor) {
            yStartCursor = await this.fetchFirstEmployeeUid();
        }

        return [
            { axisId: 'A', value: 0 },
            { axisId: 'B', value: this._cursorX.toISODate()! },
            { axisId: 'C0', value: 0 },
            { axisId: 'C1', value: 0 },
            { axisId: 'C2', value: 0 },
            { axisId: 'C3', value: 0 },
            { axisId: 'D', value: yStartCursor },
        ];
    }

    protected getSectionSizeVariables(): Record<string, string> {
        return RESOURCE_PLANNING_SECTION_SIZES;
    }

    /**
     * Substitute the default ToDo issue-filters toolbar item with the resource-slot
     * variant, so reset dispatches RESOURCE_FILTERS_CLEAR and the applied-count reads
     * `numberOfAppliedResourceFilters` (never the ToDo `filters` slot). The other
     * default items (reset-to-today, compact-view, standard-view, detail-view, add-entity-menu)
     * are kept unchanged. Substituting by id (not rebuilding the array) keeps the other
     * items in sync if the default list changes.
     */
    protected override getToolbarItems(): TimelineToolbarItem[] {
        return defaultTimelineToolbarItems().map((item) =>
            item.id === 'issue-filters' ? resourceFiltersToolbarItem : item
        );
    }

    /**
     * Fetches the first Employee SID from backend for initial Y-axis positioning.
     */
    private async fetchFirstEmployeeUid(): Promise<string> {
        const axisDataClient = container.resolve(AxisDataClient);
        const response = await axisDataClient.fetchAxisData({
            entityType: 'Employee',
            referenceUid: null,
            offset: 0,
            limit: 1,
            sort: { strategy: 'property', path: 'lastName', direction: 'ASC' }
        });

        if (response.values.length > 0) {
            const firstShortId = response.values[0][1];
            appState.setViewCursorY(this.viewStateName, firstShortId);
            return firstShortId;
        }

        return '';
    }

    /**
     * Extract Issue from the ResourcePlanningItem wrapper (one issue can produce
     * multiple items, so the item's `.data` is the wrapper, not the Issue itself —
     * see ResourcePlanningItem). Used by the base class's handleContentCellClicked()
     * to build the issue list for the edit dialog.
     */
    protected override extractIssueFromItem(item: ViewportItem<unknown>): Issue {
        return (item.data as ResourcePlanningItem).issue;
    }

    /**
     * Override hover handler: extract Issue from ResourcePlanningItem wrapper.
     */
    protected override handleContentCellHover(detail: CellHoverEventDetail): void {
        if (detail.state === 'enter') {
            const targetOrFirst = detail.targetItem ?? detail.items?.[0];
            if (targetOrFirst) {
                const rpItem = targetOrFirst.data as ResourcePlanningItem;
                UiHelper.popover(html`
                    <ha-issue-preview .issue=${rpItem.issue}></ha-issue-preview>
                `);
            }
        } else {
            UiHelper.popover(null);
        }
    }

    private _onHighlightButtonClick = (evt: Event): void => {
        const target = (evt.target as HTMLElement).closest('[data-role="highlight"]') as HTMLElement | null;
        if (!target) return;

        const uid = target.dataset.entityUid;
        if (!uid) return;

        const updated = new Set(this._highlightedUids);
        if (updated.has(uid)) {
            updated.delete(uid);
        } else {
            updated.add(uid);
        }
        this._highlightedUids = updated;

        const timeline = this.querySelector('o2d-timeline');
        if (timeline) {
            const area1 = timeline.querySelector('#grid-area-1') as LitElement | null;
            const area7 = timeline.querySelector('#grid-area-7') as LitElement | null;
            area1?.requestUpdate();
            area7?.requestUpdate();
        }
    };
}

declare global {
    interface HTMLElementTagNameMap {
        'o2d-resource-planning-view': ResourcePlanningView;
    }
}
